import moment from 'moment-timezone'
import { useContext } from 'react'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { useMeetingStatusForDetail } from 'components/meetings/hooks'
import { dateNotationSelector, timeNotationSelector, timezoneSelector } from 'selectors/current_user'
import { parseTime, userDateFormatCheckYear } from 'services/time_format'
import { useCurrentTime } from 'shared/contexts'
import { summarizeInterval } from 'shared/utils'

import { MeetingDetailDependenciesContext } from '../meeting_detail_context'
import { getExpiresAtText } from '../utils'
import { MeetingDetailHeaderView } from './meeting_detail_header_view'

export const MeetingDetailHeader = ({
  selectedEvent: event,
  todayMeetings,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent,
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies) {
    throw new Error('MeetingDetailHeader must be wrapped in a MeetingDetailDependenciesProvider')
  }
  const { NotetakerProvider, StatusIndicator } = dependencies
  const timezone = useSelector(timezoneSelector)
  const timeNotation = useSelector(timeNotationSelector)
  const dateNotation = useSelector(dateNotationSelector)
  const { data: currentUser } = useGetUserQuery()
  const isNotPretending = currentUser && !currentUser.is_pretending

  const now = useCurrentTime().tz(timezone)
  const startMmt = moment(event.startTime).tz(timezone)
  const endMmt = moment(event.endTime).tz(timezone)
  const inProgress = now.isBetween(startMmt, endMmt, 'minute', '[]')
  const isFutureMeeting = now.isBefore(startMmt)

  const meetingStatus = useMeetingStatusForDetail({
    event,
    todayMeetings,
    dependencies,
  })

  const formattedDate = startMmt.format('dddd, ')
    + userDateFormatCheckYear(startMmt, dateNotation, 'date_medium', 'date_large')
  const formattedTimeRange = summarizeInterval(
    { from: startMmt, to: endMmt },
    timeNotation,
    true
  )
  const eventTypeTimeRange = event.showEventTypeTime
    && summarizeInterval(
      {
        from: parseTime(event.eventTypeStartTime, timeNotation),
        to: parseTime(event.eventTypeEndTime, timeNotation),
      },
      timeNotation
    )
  const eventTypeTimezoneAbbr = event.showEventTypeTime
    ? moment.tz(event.eventTypeTimezoneIdentifier).format('z')
    : null
  const expiresAtText = getExpiresAtText({
    expiresAt: event.expiresAt,
    expired: event.expired,
    timezone,
    dateNotation,
  })

  return (
    <MeetingDetailHeaderView
      event={event}
      todayMeetings={todayMeetings}
      meetingStatus={meetingStatus}
      showReportModal={showReportModal}
      clearSelectedEvent={clearSelectedEvent}
      updateSelectedEvent={updateSelectedEvent}
      inProgress={inProgress}
      isFutureMeeting={isFutureMeeting}
      isNotPretending={isNotPretending}
      startTime={startMmt}
      now={now}
      timezone={timezone}
      formattedDate={formattedDate}
      formattedTimeRange={formattedTimeRange}
      eventTypeTimeRange={eventTypeTimeRange}
      eventTypeTimezoneAbbr={eventTypeTimezoneAbbr}
      expiresAtText={expiresAtText}
      NotetakerProvider={NotetakerProvider}
      StatusIndicator={StatusIndicator}
    />
  )
}
